import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { nameToHash } from '@hub/utils/contest-utils';
import { ContestDashboardSegments } from '@monorepo/client-types';
import {
  Card,
  Flex,
  Label,
  Statistic,
  TextComponent,
} from '@monorepo/react-components';
import { LoadingState, useLoading, usePagination } from '@monorepo/react-hooks';
import { Application, OpenPosition, TimeZone } from '@monorepo/types';
import { Table } from '../../components/table/table';
import { useStore } from '../../helpers/use-store';
import TradesTable from '../trades-table/trades-table';
import { POSITIONS_TABLE_SCROLL_HEIGHT } from './positions.constants';
import { PositionSideTag } from './positions.util';

import './trades-positions.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface Props {
  application?: Application;
  accountId: string;
  hideSwap?: boolean;
  expandTrades?: boolean;
  contest?: boolean;
}

const OpenPositionsTable: FunctionComponent<Props> = ({
  accountId,
  application,
  expandTrades = true,
  hideSwap = false,
  contest = false,
}) => {
  const {
    dataStore: { positionStore },
  } = useStore();
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const { loadingState, updateLoadingState } = useLoading();
  const { page, limit, total, setTotal, setLimit, setPage } = usePagination();
  const { modal } = App.useApp();
  const { t } = useTranslation(['position', 'common']);

  const isThe5ers = application === Application.THE5ERS;
  const relevantGetOpenPositions = contest
    ? positionStore.getContestOpenPositions
    : positionStore.getOpenPositions;

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const result = await relevantGetOpenPositions(accountId, {
          page,
          limit,
        });

        setOpenPositions(result.results);
        setTotal(result.total);
      } catch (e) {
        console.error(e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId, page, limit]);

  const shouldHidePnl = openPositions.every(
    (position) => position.profitAndLoss === 0,
  );

  const columns: ColumnsType<OpenPosition> = [
    {
      title: t('position:positions.id'),
      dataIndex: 'id',
      render: (_, position) => {
        return (
          <TextComponent level={8} responsive={false} weight={600}>
            {position.id}
          </TextComponent>
        );
      },
    },
    {
      title: t('common:date'),
      dataIndex: 'date',
      render: (_, position) => {
        let date = dayjs(position.date).format('MMM D hh:mm A');

        if (isThe5ers) {
          date = dayjs(position.date)
            .tz(TimeZone.Kyiv)
            .format('MMM D HH:mm:ss');
        }

        return <span>{date}</span>;
      },
    },
    {
      title: t('position:positions.symbol'),
      dataIndex: 'symbol',
      render: (_, position) => {
        return <Label color="colorInfo">{position.symbol}</Label>;
      },
    },
    {
      title: t('position:positions.side'),
      dataIndex: 'side',
      render: (_, position) => {
        return <PositionSideTag side={position.side} />;
      },
    },
    {
      title: t('position:positions.entry'),
      dataIndex: 'entry',
      render: (_, position) => {
        return (
          <TextComponent textAlign="center">
            <Statistic value={position.entry} precision={isThe5ers ? 4 : 2} />
          </TextComponent>
        );
      },
    },
    {
      title: t('position:positions.qty'),
      dataIndex: 'quantity',
    },
    {
      title: t('common:fee'),
      dataIndex: 'fee',
      render: (_, position) => {
        return (
          <Statistic value={position.fee || 0} precision={2} prefix={'$'} />
        );
      },
    },
    !hideSwap
      ? {
          title: t('position:positions.swap'),
          dataIndex: 'swap',
          render: (_, position) => {
            return (
              <Statistic
                value={position.swap || 0}
                precision={2}
                prefix={'$'}
              />
            );
          },
        }
      : {},
    shouldHidePnl
      ? {}
      : {
          title: t('common:pnl'),
          dataIndex: 'profitAndLoss',
          render: (_, position) => {
            return (
              <Statistic
                value={position.profitAndLoss || 0}
                precision={2}
                prefix={'$'}
              />
            );
          },
        },
  ];

  const isLoading = loadingState === LoadingState.Loading;
  return (
    <Card
      className="trades-positions"
      hoverable={false}
      id={nameToHash(ContestDashboardSegments.trades)}
    >
      <Flex vertical gap={16}>
        <TextComponent responsive={false} level={4}>
          {t('position:positions.open_trades')}
        </TextComponent>
        <Table
          size={'middle'}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50],
            current: page,
            pageSize: limit,
            total,
          }}
          onChange={(pagination) => {
            if (pagination.current) setPage(pagination.current);
            if (pagination.pageSize) setLimit(pagination.pageSize);
          }}
          scroll={{ y: POSITIONS_TABLE_SCROLL_HEIGHT }}
          onRow={(record: OpenPosition) => {
            return {
              onClick: () => {
                if (!expandTrades) {
                  return;
                }

                modal.info({
                  width: '80%',
                  closable: true,
                  maskClosable: true,
                  title: t('position:positions.position_details_for', {
                    symbol: record.symbol,
                  }),
                  content: (
                    <TradesTable
                      positionState={'open'}
                      accountId={accountId}
                      positionId={record.id}
                    />
                  ),
                  okText: t('common:close'),
                });
              },
            };
          }}
          loading={isLoading}
          columns={columns.filter((col) => col.title)}
          dataSource={openPositions}
          rowKey="id"
          rowHoverable={expandTrades}
        />
      </Flex>
    </Card>
  );
};

export default OpenPositionsTable;
